/**
 * DOCUMENTATION:
 * Component: DesignPatternsBehavioral
 * Purpose: Educational page teaching the Observer and Strategy design patterns.
 * * * Note for Junior Developers:
 * - Observer: "The Newsroom." One object (the Subject) maintains a list of dependents (Observers)
 * and notifies them of any state changes. This is the heart of React state!
 * - Strategy: "The Multi-Tool." You define a family of algorithms and make them interchangeable.
 * This lets you swap logic (like 'Standard Shipping' vs 'Express Shipping') without
 * messy if/else chains.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Fab, Zoom, Tooltip, Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1508921234172-b68ed335b3e6";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

/* ✅ Main Component */
export default function DesignPatternsBehavioral() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const patterns = useMemo(() => [
        {
            name: "Observer",
            intent: "Define a one-to-many dependency between objects so that when one object changes state, all its dependents are notified and updated automatically.",
            useCase: "Redux state updates, DOM event listeners (onClick), and Pub/Sub messaging systems."
        },
        {
            name: "Strategy",
            intent: "Define a family of algorithms, encapsulate each one, and make them interchangeable. Strategy lets the algorithm vary independently from clients that use it.",
            useCase: "Payment processing (PayPal vs. Stripe), file compression formats, or different pathfinding algorithms in a game."
        }
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. The Observer Pattern (Simple Pub/Sub)",
            code: `// ✅ The Subject (The Newsroom)\nclass NewsAgency {\n  constructor() {\n    this.subscribers = [];\n  }\n\n  subscribe(observer) {\n    this.subscribers.push(observer);\n  }\n\n  notify(news) {\n    this.subscribers.forEach(sub => sub.update(news));\n  }\n}\n\n// ✅ The Observers (The Readers)\nclass Reader {\n  constructor(name) {\n    this.name = name;\n  }\n  update(news) {\n    console.log(\`\${this.name} just read: \${news}\`);\n  }\n}\n\nconst agency = new NewsAgency();\nconst kofi = new Reader("Kofi");\nconst ama = new Reader("Ama");\n\nagency.subscribe(kofi);\nagency.subscribe(ama);\nagency.notify("Design Patterns are cool!");`,
            desc: "The Observer pattern is the backbone of reactive programming. Instead of an object constantly asking 'Is the data ready?', it simply waits for the Subject to announce it."
        },
        {
            title: "2. The Strategy Pattern (Algorithm Swapping)",
            code: `// ✅ Different "Strategies" (Algorithms)\nconst creditCard = (amount) => console.log(\`Paid \${amount} via Credit Card.\`);\nconst payPal = (amount) => console.log(\`Paid \${amount} via PayPal.\`);\n\n// ✅ The Context (The Shopping Cart)\nclass Checkout {\n  constructor(strategy) {\n    this.strategy = strategy;\n  }\n\n  setStrategy(strategy) {\n    this.strategy = strategy;\n  }\n\n  execute(amount) {\n    this.strategy(amount);\n  }\n}\n\n// Usage:\nconst cart = new Checkout(creditCard);\ncart.execute(100); \n\ncart.setStrategy(payPal);\ncart.execute(100); // Same action, different logic!`,
            desc: "The Strategy pattern helps you follow the Open/Closed principle. You can add a 'Crypto' payment strategy tomorrow without ever touching the logic inside the 'Checkout' class."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    BEHAVIORAL PATTERNS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Orchestrating Interaction
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Behavioral patterns are concerned with the assignment of responsibilities between objects. They don't just describe patterns of objects or classes but also the patterns of <strong>communication</strong> between them.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>Communication Blueprints</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            {patterns.map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", height: "100%", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                        <Typography variant="subtitle1" sx={{ color: "goldenrod", fontWeight: 800 }}>{item.name}</Typography>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>{item.intent}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>USE CASE: {item.useCase}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>



                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/design-patterns-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ backgroundColor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px", "&:hover": { backgroundColor: "#e0b200" } }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
